<template>
  <property-report-card
    title="Unit-Level Insights"
    description="Explore competitor pricing and concessions at the individual unit level."
    :printing-mode="printingMode"
    :is-empty="unitsWithProperties.length === 0"
  >
    <template #actions>
      <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
      <q-btn v-if="!hideFilter" icon="add" no-caps label="Add Filter" flat class="q-my-auto">
        <q-menu :auto-close="false" transition-duration="100" @hide="createFilterHide">
          <q-list v-if="!selectedFilter.filter" dense>
            <q-item
              v-for="f in filterOptions"
              :key="f.label"
              clickable
              @click="
                () => {
                  selectedFilter.minValue = f.minValue ?? null
                  selectedFilter.maxValue = f.maxValue ?? null
                  selectedFilter.filter = f
                  $tracker.changedFilter(f.label, 'unit_details', null)
                }
              "
            >
              <q-item-section>{{ f.label }}</q-item-section>
            </q-item>
          </q-list>
          <filter-popover
            v-else
            :selected-filter="selectedFilter"
            @update:values="selectedFilter.values = $event"
            @update:min-value="selectedFilter.minValue = $event"
            @update:max-value="selectedFilter.maxValue = $event"
          />
        </q-menu>
      </q-btn>
      <q-chip
        v-for="(f, idx) in [selectedFilter, ...activeFilters].filter((_) => _.filter)"
        :key="idx"
        class="q-my-auto"
        square
        no-caps
        outline
        clickable
        removable
        @remove="removeFilter(f.filter?.label)"
      >
        <span class="q-mr-xs">{{ f.filter?.label }}</span>
        <span v-if="f.filter?.type === 'select'">({{ f.values.length }})</span>
        <span v-else-if="f.filter?.type === 'range'">
          ({{ f.minValue?.toLocaleString('en-US') }} - {{ f.maxValue?.toLocaleString('en-US') }})
        </span>
        <span v-else-if="f.filter?.type === 'date'"> ({{ computeDateRangeLabel(f) }}) </span>
        <q-menu
          :auto-close="false"
          transition-duration="100"
          @hide="editFilterHide(f.filter?.label)"
        >
          <filter-popover
            :selected-filter="f"
            @update:values="f.values = $event"
            @update:min-value="f.minValue = $event"
            @update:max-value="f.maxValue = $event"
          />
        </q-menu>
      </q-chip>
    </template>
    <q-separator />
    <unit-details-table
      id="intercom-unit-level-insights-content"
      :rows="rows"
      :expanded-unit-history="expandedUnitHistory"
      :bordered="false"
      :printing-mode="printingMode"
      :date-range="dateRange"
    />
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { PropertyDetailsResponse, Availability, AvailabilityPeriod, Price } from 'common'
import { format, parseISO, max, addDays } from 'date-fns'
import { daysOnMarket, getEffPsf, getPsf } from 'common'

import FilterPopover from '@/components/PropertyReports/FilterPopover.vue'
import UnitDetailsTable, { exportCSV } from '@/components/PropertyReports/UnitDetailsTable.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import { unitsFound } from '@/utils.ts'

type SelectedFilter = {
  filter: {
    label: string
    type: string
    options: any[]
    isValid: (row: Row) => boolean
  } | null
  values: (string | number)[]
  minValue: null | number | Date
  maxValue: null | number | Date
}
type Row = {
  unit: Availability
  prop: PropertyDetailsResponse
  period: AvailabilityPeriod
}

export default {
  name: 'PropertyCompareUnitDetails',
  components: {
    PropertyReportCard,
    FilterPopover,
    UnitDetailsTable
  },
  props: {
    unitsWithProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        unit: Availability
        lastHistory: Price
        period: AvailabilityPeriod
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    hideFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    dateRange: {
      type: Object as PropType<{
        from: Date
        to: Date
      }>,
      required: true
    }
  },
  data() {
    return {
      selectedFilter: {
        filter: null,
        values: [],
        minValue: null,
        maxValue: null
      } as SelectedFilter,
      activeFilters: [] as SelectedFilter[]
    }
  },
  computed: {
    filterOptions() {
      const activeFilterLabels = this.activeFilters.map((_) => _.filter?.label)
      return [
        {
          label: 'Zip Code',
          type: 'select',
          getter: (row: Row) => row.prop.zip_code
        },
        {
          label: 'Floorplan',
          type: 'select',
          getter: (row: Row) => row.unit.floorplan_name
        },
        { label: 'Unit', type: 'select', getter: (row: Row) => row.unit.unit_name },
        { label: 'Floor', type: 'range', getter: (row: Row) => [row.unit.floor, row.unit.floor] },
        {
          label: 'Type',
          type: 'select',
          getter: (row: Row) =>
            `${row.unit.bed}/${row.unit.bath}${row.unit.partial_bath ? '.5' : ''}`
        },
        {
          label: 'SF',
          type: 'range',
          getter: (row: Row) => [
            row.unit.min_sqft ? row.unit.min_sqft : row.unit.sqft,
            row.unit.max_sqft ? row.unit.max_sqft : row.unit.sqft
          ]
        },
        {
          label: 'Available On',
          type: 'date',
          getter: (row: Row) => {
            if (row.period.availability && row.period.exit_market) {
              const avail = parseISO(row.period.availability)
              const exit = parseISO(row.period.exit_market)
              return [avail, max([exit, avail])]
            }
            if (row.period.availability) {
              const avail = parseISO(row.period.availability)
              const exit = max([new Date(), avail])
              return [avail, exit]
            }
            return [null, null]
          },
          minValue: addDays(new Date(), -30),
          maxValue: new Date()
        },
        {
          label: 'Last Asking Rent',
          type: 'range',
          getter: (row: Row) => {
            const prices = row.unit.history.filter((_) => _.period_id === row.period.period_id)
            const lastPrice = prices.slice(-1)[0]
            const price = lastPrice.price || lastPrice.min_price
            return [price, price]
          }
        },
        {
          label: 'Asking Rent PSF',
          type: 'range',
          getter: (row: Row) => {
            const prices = row.unit.history.filter((_) => _.period_id === row.period.period_id)
            const lastPrice = prices.slice(-1)[0]
            const effPrice = getPsf({ ...row.unit, ...lastPrice })
            return [effPrice, effPrice]
          }
        },
        {
          label: 'Last Effective Rent',
          type: 'range',
          getter: (row: Row) => {
            const prices = row.unit.history.filter((_) => _.period_id === row.period.period_id)
            const lastPrice = prices.slice(-1)[0]
            const effPrice = lastPrice.effective_price || lastPrice.min_effective_price
            return [effPrice, effPrice]
          }
        },
        {
          label: 'Effective Rent PSF',
          type: 'range',
          getter: (row: Row) => {
            const prices = row.unit.history.filter((_) => _.period_id === row.period.period_id)
            const lastPrice = prices.slice(-1)[0]
            const effPrice = getEffPsf({ ...row.unit, ...lastPrice })
            return [effPrice, effPrice]
          }
        },
        {
          label: 'Days on Market',
          type: 'range',
          getter: (row: Row) => {
            const dom = daysOnMarket(row.period, new Date())
            return [dom, dom]
          }
        },
        {
          label: 'Listings',
          type: 'select',
          getter: (row: Row) => (row.period.exit_market ? 'Closed' : 'Active')
        }
      ]
        .map((f) => ({
          label: f.label,
          type: f.type,
          options: [...new Set(this.rows.map(f.getter).filter((_) => _ !== null))].sort(),
          isValid: (row: Row) => {
            const filter = this.activeFilters.find((_) => _.filter?.label === f.label)
            if (!filter) {
              return true
            }
            const val = f.getter(row)
            if (f.type === 'select') {
              return filter.values.includes(val !== null ? val : '')
            } else if (f.type === 'range') {
              const x = filter.minValue ? (val[0] || 0) >= filter.minValue : true
              const y = filter.maxValue ? (val[1] || 0) <= filter.maxValue : true
              return x && y
            } else if (f.type === 'date') {
              return !(filter.minValue > val[1] || filter.maxValue < val[0])
            }
            return false
          },
          minValue: f.minValue,
          maxValue: f.maxValue
        }))
        .filter((_) => !activeFilterLabels.includes(_.label))
    },
    rows() {
      return this.unitsWithProperties.filter(
        (row) =>
          this.activeFilters.map((filter) => filter.filter?.isValid(row)).filter((_) => !_)
            .length === 0
      )
    }
  },
  methods: {
    unitsFound,
    exportCSV() {
      exportCSV(this.rows, 'unit-details')
      this.$tracker.exported('unit_details')
    },
    createFilterHide() {
      if (
        this.selectedFilter.values.length ||
        this.selectedFilter.minValue !== null ||
        this.selectedFilter.maxValue !== null
      ) {
        this.activeFilters = [this.selectedFilter, ...this.activeFilters]
      }
      this.selectedFilter = {
        filter: null,
        values: [],
        minValue: null,
        maxValue: null
      }
    },
    removeFilter(label?: string) {
      this.activeFilters = this.activeFilters.filter((_) => _.filter?.label !== label)
    },
    editFilterHide(label?: string) {
      const f = this.activeFilters.find((_) => _.filter?.label === label)
      if (f && (f.values.length || f.minValue !== null || f.maxValue !== null)) {
        return
      }
      this.removeFilter(label)
    },
    computeDateRangeLabel(value: SelectedFilter) {
      if (value.minValue && value.maxValue && value.minValue !== value.maxValue) {
        return `${format(value.minValue as Date, 'M/d/yy')} → ${format(value.maxValue as Date, 'M/d/yy')}`
      } else if (value.minValue) {
        return format(value.minValue as Date, 'M/d/yy')
      }
      return ''
    }
  }
}
</script>
